/**
 * Print mode.
 *
 * Paper is always the light theme, but a Mermaid diagram bakes its colors into the SVG at render
 * time — printing from dark mode would put black rectangles on the page. `@media print` cannot reach
 * those colors, and `beforeprint` fires too late for an async re-render to land. So the print
 * button flips this flag, waits for the diagrams to redraw, and only then opens the print dialog.
 *
 * The flag also puts Ming's `.light` class on <html>, so every token resolves to its light value on
 * paper. `beforeprint` covers Ctrl+P, where CSS still switches in time even if diagrams can't.
 */
let printing = false;

const listeners = new Set<() => void>();

const set = (value: boolean): void => {
  printing = value;
  document.documentElement.classList.toggle("light", value);

  for (const listener of listeners) listener();
};

export const isPrinting = (): boolean => printing;

export const subscribePrintMode = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

window.addEventListener("beforeprint", () => {
  document.documentElement.classList.add("light");
});
window.addEventListener("afterprint", () => {
  if (!printing) document.documentElement.classList.remove("light");
});

/** One paint plus a beat, which is enough for the mermaid renders queued by the flag to settle. */
const settle = (): Promise<void> =>
  new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 250);
    });
  });

export const printDocument = async (): Promise<void> => {
  set(true);

  try {
    await settle();
    window.print();
  } finally {
    set(false);
  }
};
